/**
 * Reglas de stock del merch propio, sin base de datos.
 * El SQL de la migración aplica las mismas decisiones.
 */

export const LOT_SLUG = 'pack-lote-completo-pro';

export const LOT_GARMENTS = [
    { slug: 'camiseta-tecnica-corta', label: 'CORTA' },
    { slug: 'camiseta-tecnica-larga', label: 'LARGA' },
    { slug: 'sudadera', label: 'SUDADERA' },
] as const;

export const MERCH_SIZES = ['S', 'M', 'L', 'XL', 'XXL'] as const;

const SHIPPED = new Set(['shipped', 'delivered', 'completed']);
const UNSHIPPED_PAID = new Set(['paid', 'processing']);

export function stockTotal(available: number, committed: number): number {
    return available + committed;
}

/** Con el interruptor apagado no hay rechazo, aunque el recuento sea 0. */
export function checkoutRejects(enforced: boolean, available: number | null, qty: number): boolean {
    if (!enforced || available == null) return false;
    return available < qty;
}

export function packStockVariant(variantId: string, sourceVariantId: string | null): string {
    return sourceVariantId ?? variantId;
}

/** El pack no tiene almacén propio: enseña el nombre bajo la prenda base. */
const PACK_BASE_SLUG: Record<string, string> = {
    'pack-camiseta-corta-pro': 'camiseta-tecnica-corta',
    'pack-camiseta-larga-pro': 'camiseta-tecnica-larga',
    'pack-sudadera-pro': 'sudadera',
    'pack-gorra-pro': 'gorra',
};

export function packBaseSlug(packSlug: string): string | null {
    return PACK_BASE_SLUG[packSlug] ?? null;
}

export function garmentPackNames(
    products: readonly { slug: string; name: string; kind: string }[],
): Map<string, string[]> {
    const names = new Map<string, string[]>();
    for (const product of products) {
        if (product.kind !== 'pack') continue;
        const base = packBaseSlug(product.slug);
        if (!base) continue;
        const list = names.get(base) ?? [];
        list.push(product.name);
        names.set(base, list);
    }
    return names;
}

/** Mientras se escribe el recuento: contado por debajo de lo ya vendido y no enviado. */
export function countBelowCommitted(raw: string, committed: number): boolean {
    const trimmed = raw.trim();
    if (!/^\d+$/.test(trimmed)) return false;
    return Number(trimmed) < committed;
}

export interface CountDecision {
    save: boolean;
    stock: number | null;
    oversold: boolean;
    shortfall: number;
}

/**
 * stock = contado − comprometido. Si sale negativo no se guarda
 * hasta que quien cuenta acepta dejarlo en 0 y marcar sobreventa.
 */
export function initialCountDecision(
    counted: number,
    committed: number,
    acceptOversell: boolean,
): CountDecision {
    const raw = counted - committed;
    if (raw >= 0) {
        return { save: true, stock: raw, oversold: false, shortfall: 0 };
    }
    if (!acceptOversell) {
        return { save: false, stock: null, oversold: true, shortfall: committed - counted };
    }
    return { save: true, stock: 0, oversold: true, shortfall: committed - counted };
}

export function saleMovementKey(orderItemId: string, variantId: string): string {
    return `${orderItemId}:${variantId}:sale`;
}

/** Una segunda aplicación de la misma venta no descuenta otra vez. */
export function saleAlreadyApplied(existingKeys: readonly string[], orderItemId: string, variantId: string): boolean {
    return existingKeys.includes(saleMovementKey(orderItemId, variantId));
}

/** Reponer solo si el interruptor está encendido y la prenda vuelve al almacén. */
export function shouldRestock(input: {
    enforced: boolean;
    fromStatus: string;
    toStatus: 'cancelled' | 'refunded';
    returnedToWarehouse: boolean;
}): boolean {
    if (!input.enforced) return false;
    if (input.toStatus === 'cancelled') return UNSHIPPED_PAID.has(input.fromStatus);
    if (input.toStatus !== 'refunded') return false;
    if (UNSHIPPED_PAID.has(input.fromStatus)) return true;
    if (SHIPPED.has(input.fromStatus)) return input.returnedToWarehouse;
    return false;
}

export function chestSubtypeAvailable(input: {
    enforced: boolean;
    quotaOpen: boolean;
    someSizeAvailable: boolean;
}): boolean {
    if (!input.quotaOpen) return false;
    if (!input.enforced) return true;
    return input.someSizeAvailable;
}

function sizeAfterLabel(notes: string, label: string): string | null {
    const text = notes.toUpperCase();
    const at = text.indexOf(label);
    if (at < 0) return null;
    const tail = text.slice(at + label.length);
    const match = tail.match(/^[^A-Z]{0,6}(XXL|XL|L|M|S)([^A-Z]|$)/);
    return match ? match[1] : null;
}

/** Tallas del lote en atributos o, si no, en las notas. Si falta alguna, queda pendiente. */
export function resolveLotSizes(
    attributes: { sizes?: Record<string, string> } | null | undefined,
    notes: string | null | undefined,
): Record<string, string> | null {
    const fromAttributes = attributes?.sizes ?? {};
    const resolved: Record<string, string> = {};
    for (const garment of LOT_GARMENTS) {
        const structured = (fromAttributes[garment.slug] ?? '').trim().toUpperCase();
        const size = MERCH_SIZES.includes(structured as (typeof MERCH_SIZES)[number])
            ? structured
            : sizeAfterLabel(notes ?? '', garment.label);
        if (!size || !MERCH_SIZES.includes(size as (typeof MERCH_SIZES)[number])) return null;
        resolved[garment.slug] = size;
    }
    resolved.gorra = 'UNICA';
    return resolved;
}

export function stockTone(available: number, threshold: number): 'out' | 'low' | 'ok' {
    if (available <= 0) return 'out';
    if (available <= threshold) return 'low';
    return 'ok';
}
